import {
  FlexRow,
  DropdownContainer,
  FlexCell,
  CheckboxGroup,
  Button,
  Dropdown,
} from '@epam/loveship'
import { cx } from '@epam/uui-core'
import { IDropdownToggler } from '@epam/uui-core'
import { amplSnakeCaseEventProperties, amplTrack } from '@stlm/common-ui/lib'
import { useMemo, useState } from 'react'
import { useActions } from '../../../../hooks/redux/actions'
import { useAppSelector } from '../../../../hooks/redux/redux'
import { useIsFirstSidebarMount } from '../../../../hooks/useIsFirstSidebarMount'
import { useGetLayersQuery } from '../../../../store'
import { getMappedLayers } from '../../../../utils/layers/mapLayers'
import css from './LayersFilter.module.scss'
import { useSideBarControlls } from '../../../../hooks/useSideBarControlls'

export const LayersFilter = () => {
  const { data: layers } = useGetLayersQuery()
  const {
    addSelectedLayers,
    removeSelectedLayers,
    setButtonState,
    setMarkersCleared,
  } = useActions()
  const { showPointsPanel } = useSideBarControlls()
  const { selectedLayers, markersCleared } = useAppSelector(
    (state) => state.layers
  )
  const { isSideBarOpened, isSideBarGoingToClose } = useAppSelector(
    (state) => state.app
  )
  const { selectedPoint } = useAppSelector((state) => state.points)
  const isFirstMount = useIsFirstSidebarMount(isSideBarOpened)

  const mappedLayers = useMemo(() => getMappedLayers(layers), [layers])
  const mainCategories = mappedLayers
    ?.filter((item) => item.parentId === null)
    .sort((a, b) => a.showPriority - b.showPriority)
  // const subCategories = mappedLayers?.filter((item) => item.parentId !== null).map((item) => item.id) || [7, 8, 6, 2, 9, 5, 3, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35,
  //   36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54,
  //   55, 56, 57, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72,
  // ]

  const [selectAllChecked, setSelectAllChecked] = useState(false)
  const isClearMap = markersCleared && selectedLayers.length === 0

  const handleSelectAllChange = () => {
    const allIds =
      mappedLayers
        ?.filter((item) => item.parentId !== null)
        .map((item) => item.id) || []
    if (isClearMap) {
      if (selectAllChecked) {
        allIds.forEach((id) => removeSelectedLayers(id))
        setMarkersCleared(false)
        setSelectAllChecked(true)
      } else {
        allIds.forEach((id) => addSelectedLayers(id))
        setMarkersCleared(false)
        setSelectAllChecked(true)
      }
    } else {
      if (selectAllChecked || selectedLayers.length > 0) {
        allIds.forEach((id) => removeSelectedLayers(id))
        setMarkersCleared(true)
        setSelectAllChecked(false)
      } else {
        setMarkersCleared(true)
      }
    }
  }

  const handleCategoryChange = (id: number) => {
    if (selectedLayers.includes(id)) {
      removeSelectedLayers(id)
    } else {
      addSelectedLayers(id)
      setMarkersCleared(false)
    }
  }

  const computeDropdownValues = (
    layerId: number
  ): { value: number[]; checkedCount: number } => {
    const layerIds =
      mappedLayers
        ?.filter((item) => item.parentId === layerId)
        .map((subCategory) => subCategory.id) || []

    const value = layerIds.filter((id) => selectedLayers.includes(id))
    return {
      value,
      checkedCount: value.length,
    }
  }

  const aniMationClassName = cx(
    { show: isSideBarOpened && !isFirstMount },
    { hide: isSideBarGoingToClose && !isFirstMount }
  )

  const layersPanelCn = cx(css.layersPanel, aniMationClassName)
  const animatedDivWidthCn = cx(css.animatedDiv, aniMationClassName)

  return (
    <div className={animatedDivWidthCn}>
      <FlexRow cx={layersPanelCn} alignItems="top">
        <FlexCell width="auto">
          {mainCategories?.map((layer) => {
            const { value, checkedCount } = computeDropdownValues(layer.id)
            return (
              <Dropdown
                key={layer.id}
                renderBody={() => {
                  const subItems = mappedLayers?.filter(
                    (item) => item.parentId === layer.id
                  )
                  return subItems?.length ? (
                    <DropdownContainer
                      maxWidth={360}
                      vPadding="30"
                      padding="18"
                      cx={css.container}
                    >
                      <FlexRow>
                        <FlexCell width="auto" alignSelf="flex-start">
                          <CheckboxGroup
                            items={
                              mappedLayers
                                ?.filter((item) => item.parentId === layer.id)
                                .map((subCategory) => ({
                                  id: subCategory.id,
                                  name: subCategory.name,
                                })) || []
                            }
                            value={value}
                            onValueChange={(newValue) => {
                              const addedId = newValue.find(
                                (id) => !value?.includes(id)
                              )
                              const removedId = value.find(
                                (id) => !newValue.includes(id)
                              )
                              if (addedId) {
                                handleCategoryChange(addedId)
                                if (
                                  selectedPoint &&
                                  addedId !== selectedPoint.layerId &&
                                  !selectedLayers.includes(
                                    selectedPoint.layerId
                                  )
                                ) {
                                  showPointsPanel()
                                }
                              }
                              if (removedId) {
                                handleCategoryChange(removedId)
                                if (
                                  removedId === selectedPoint?.layerId &&
                                  selectedLayers.length !== 1
                                ) {
                                  showPointsPanel()
                                }
                              }
                              setButtonState({
                                layerId: layer.id,
                                value: newValue,
                              })
                              amplTrack({
                                name: 'map_filters_apply',
                                ...amplSnakeCaseEventProperties({
                                  [layer.name]: newValue,
                                }),
                              })
                            }}
                            direction="vertical"
                          />
                        </FlexCell>
                      </FlexRow>
                    </DropdownContainer>
                  ) : null
                }}
                renderTarget={(props: IDropdownToggler) => (
                  <div className={css.filterButtonWrapper}>
                    <Button
                      icon={layer.icon}
                      size="30"
                      fill="white"
                      color="sky"
                      caption={layer.name}
                      cx={css.layerBtn}
                      {...props}
                    />
                    {checkedCount > 0 && (
                      <div className={css.counter}>{checkedCount}</div>
                    )}
                  </div>
                )}
              />
            )
          })}
        </FlexCell>
        <FlexCell width={160} grow={1} minWidth={1}>
          <Button
            size="30"
            caption={isClearMap ? 'Select all' : 'Clear map'}
            onClick={handleSelectAllChange}
            color="sky"
            cx={css.filterButton}
          />
        </FlexCell>
      </FlexRow>
    </div>
  )
}
